import { Component, Input, inject, computed } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";

import { GLOBAL_BASE_CURRENCY } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { ScenarioAccountInfo } from "@bitwarden/web-vault/app/models/types/scenario-group.types";
import DateFormat from "@bitwarden/web-vault/app/shared/utils/helper.date/date-format";
import { ScenarioDisplayView } from "@bitwarden/web-vault/app/models/view/scenario/scenario-display.view";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

const NONE = "NONE";

@Component({
  selector: "app-future-table-tabs-info",
  templateUrl: "path-tabs-info.component.html",
})
export class PathTabsInfoComponent {
  @Input() scenarioDetails!: ScenarioDisplayView;

  scenarioTypeToPath: { [key: string]: string } = {
    currentInterestRate: "currentAccountGrowth",
    bestExistingInterestRate: "bestExistingAccount",
    bestNewInterestRate: "topRegionalAccount",
  };

  protected userStore: UserStoreService = inject(UserStoreService);

  coreCurrency = computed(
    () => this.userStore.preferences.preferenceView().coreCurrency ?? GLOBAL_BASE_CURRENCY,
  );

  constructor(
    private dateFormat: DateFormat,
    private sanitizer: DomSanitizer,
  ) {}

  protected getScenarioAccountInfo(account: ScenarioAccountInfo) {
    const accountName = this.getAccount(account);
    const rate = this.getRate(account);
    const institutionName = this.getInstitutionName(account);
    return `${institutionName} ${accountName} ${rate}`;
  }

  getInstitutionName(accountInfo: ScenarioAccountInfo) {
    return accountInfo?.accountBank ? accountInfo.accountBank : "";
  }

  getUrl(accountInfo: ScenarioAccountInfo) {
    return accountInfo?.accountUrl ? accountInfo.accountUrl : "";
  }

  getAccount(accountInfo: ScenarioAccountInfo) {
    return accountInfo?.accountName ? accountInfo.accountName : "";
  }

  getLastCheckedDate(accountInfo: ScenarioAccountInfo) {
    const hasAccountInfo = accountInfo?.rate && accountInfo?.rate.length > 0;
    return hasAccountInfo ? accountInfo.rate[0].last_updated : "";
  }

  // resorted to using innerHTML to avoid more method calls
  getLastCheckedDateMarkup(accountInfo: ScenarioAccountInfo) {
    const lastCheckedDate = this.getLastCheckedDate(accountInfo);
    const relativeDays = lastCheckedDate && this.dateFormat.toDateWithRelativeDays(lastCheckedDate);
    if (typeof relativeDays === "object") {
      const { date, duration } = relativeDays;
      return this.sanitizer.bypassSecurityTrustHtml(`<div>${date}</div><div>(${duration})</div>`);
    }
    return ``;
  }

  cleanDescription(descList: string[]) {
    return descList.filter((item) => item.length !== 0 && item.trim() !== NONE);
  }

  getScenarioAccountConditions(accountInfo: ScenarioAccountInfo) {
    const accountInfoRate = accountInfo.rate[0];
    const intrinsicDesc = accountInfoRate?.condition_intrinsic_desc.trim().split("\n");
    const actionDesc = accountInfoRate?.condition_action_desc.trim().split("\r\n*");
    const allDesc = intrinsicDesc.concat(actionDesc);
    return this.cleanDescription(allDesc);
  }

  getRate(accountInfo: ScenarioAccountInfo) {
    if (accountInfo.rate && accountInfo.rate.length > 0) {
      const maxRate = accountInfo.rate.reduce((max, current) => {
        return current.rate > max ? current.rate : max;
      }, accountInfo.rate[0].rate);
      const minRate = accountInfo.rate.reduce((min, current) => {
        return current.rate < min ? current.rate : min;
      }, accountInfo.rate[0].rate);

      const rate = accountInfo.rate.length > 1 ? `${minRate} - ${maxRate}` : `${maxRate}`;
      return `(${rate}% p.a.)`;
    } else {
      return "(unknown account type)";
    }
  }
}
